/** @jsxImportSource @emotion/react */
import * as s from './style';
import { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AutoTypeText from '../../components/main/AutoTypeText';
import GalleryLinkMenu from '../../components/main/gallery_link/GalleryLinkMenu';
import { createLog } from '../../atoms/log/LogFunction';
import { useRecoilState } from 'recoil';
import { logTokenAtom } from '../../atoms/log/LogTokenStore';
import CommonButton from '../../components/atoms/button/CommonButton';
import { Science, Map, Insights, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

const Main = () => {
    const location = useLocation();
    const [logTokenState, setLogTokenState] = useRecoilState(logTokenAtom);
    const ref = useRef();
    const navigate = useNavigate();

    // 드롭다운 상태 관리
    const [isLab1DropdownOpen, setIsLab1DropdownOpen] = useState(false);
    const [isLab2DropdownOpen, setIsLab2DropdownOpen] = useState(false);
    const [isMyMapDropdownOpen, setIsMyMapDropdownOpen] = useState(false);
    const lab1DropdownRef = useRef(null);
    const lab2DropdownRef = useRef(null);
    const myMapDropdownRef = useRef(null);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (lab1DropdownRef.current && !lab1DropdownRef.current.contains(event.target)) {
                setIsLab1DropdownOpen(false);
            }
            if (lab2DropdownRef.current && !lab2DropdownRef.current.contains(event.target)) {
                setIsLab2DropdownOpen(false);
            }
            if (myMapDropdownRef.current && !myMapDropdownRef.current.contains(event.target)) {
                setIsMyMapDropdownOpen(false);
            }
        };

        if (isLab1DropdownOpen || isLab2DropdownOpen || isMyMapDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLab1DropdownOpen, isLab2DropdownOpen, isMyMapDropdownOpen]);

    const onClickMapLinkImg = async (categoryCd) => {
        navigate('/sodamap', { state: { categoryCd: categoryCd } });

        const logOption = {
            page: location.pathname,
            eventName: 'start_the_map',
            eventValue: categoryCd,
            sessionToken: logTokenState,
        };

        const newToken = await createLog(logOption);
        setLogTokenState(newToken);
    };

    const onClickLab1ButtonHandle = () => {
        setIsLab1DropdownOpen(!isLab1DropdownOpen);
        setIsLab2DropdownOpen(false); // 다른 드롭다운 닫기
        setIsMyMapDropdownOpen(false);
    };

    const onClickLab2ButtonHandle = () => {
        setIsLab2DropdownOpen(!isLab2DropdownOpen);
        setIsLab1DropdownOpen(false); // 다른 드롭다운 닫기
        setIsMyMapDropdownOpen(false);
    };

    const onClickMyMapButtonHandle = () => {
        setIsMyMapDropdownOpen(!isMyMapDropdownOpen);
        setIsLab1DropdownOpen(false); // 다른 드롭다운 닫기
        setIsLab2DropdownOpen(false); // 다른 드롭다운 닫기
    };

    const onClickLab1SubmenuItem = (path) => {
        navigate(path, { replace: true });
        setIsLab1DropdownOpen(false);
    };

    const onClickLab2SubmenuItem = (path) => {
        navigate(path, { replace: true });
        setIsLab2DropdownOpen(false);
    };

    const onClickMyMapSubmenuItem = (path) => {
        navigate(path, { replace: true });
        setIsMyMapDropdownOpen(false);
    };

    return (
        <div css={s.container} ref={ref}>
            <div css={s.bannerContainer}>
                <div css={s.bannerTextContainer}>
                    <h1 css={s.title}>
                        Look at the map,
                        <br />
                        Know the indicators
                        <br />
                        And get insights.
                    </h1>
                    <div css={s.mainText}>
                        <span>SO</span>
                        <AutoTypeText text={'cial'} />
                        <span>DA</span>
                        <AutoTypeText text={'ta  '} />
                        <span>MAP</span>
                    </div>
                    <p css={s.subText}>
                        지도로 볼 수 있는 모든 데이터를 시각화하여 지역의 최신 정보 및 이슈,
                        <br />
                        필요한 정보를 바로바로 보여줍니다.
                    </p>
                    <div css={s.buttonContainer}>
                        <div css={s.dropdownContainer} ref={lab1DropdownRef}>
                            <CommonButton
                                variant="navigation"
                                navigationType="lab1"
                                startIcon={<Insights />}
                                endIcon={isLab1DropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                onClick={onClickLab1ButtonHandle}
                            >
                                통계 비교·분석
                            </CommonButton>
                            {isLab1DropdownOpen && (
                                <div css={s.dropdownMenu}>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => onClickLab1SubmenuItem('/lab/lab_1/mode_select')}
                                    >
                                        통계 비교
                                    </div>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => onClickLab1SubmenuItem('/lab/lab_1/point_data')}
                                    >
                                        통계 분석
                                    </div>
                                </div>
                            )}
                        </div>
                        <div css={s.dropdownContainer} ref={myMapDropdownRef}>
                            <CommonButton
                                variant="navigation"
                                navigationType="address"
                                startIcon={<Map />}
                                endIcon={isMyMapDropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                onClick={onClickMyMapButtonHandle}
                            >
                                내 지도 만들기
                            </CommonButton>
                            {isMyMapDropdownOpen && (
                                <div css={s.dropdownMenu}>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => onClickMyMapSubmenuItem('/my_map/geocoder')}
                                    >
                                        주소 찾기
                                    </div>
                                    <div
                                        css={s.dropdownItem}
                                        // onClick={() => onClickMyMapSubmenuItem('/lab/lab_2/user_data')}
                                        onClick={() => onClickMyMapSubmenuItem('/my_map/user_data')}
                                    >
                                        사용자 데이터
                                    </div>
                                </div>
                            )}
                        </div>
                        <div css={s.dropdownContainer} ref={lab2DropdownRef}>
                            <CommonButton
                                variant="navigation"
                                navigationType="lab2"
                                startIcon={<Science />}
                                endIcon={isLab2DropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                onClick={onClickLab2ButtonHandle}
                            >
                                
                                실험실
                            </CommonButton>
                            {isLab2DropdownOpen && (
                                <div css={s.dropdownMenu}>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => onClickLab2SubmenuItem('/lab/lab_2/maps/population')}
                                    >
                                        인구 정보
                                    </div>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => onClickLab2SubmenuItem('/lab/lab_2/maps/housing')}
                                    >
                                        주택 정보
                                    </div>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => onClickLab2SubmenuItem('/lab/lab_2/maps/lifestyle')}
                                    >
                                        생활 정보
                                    </div>
                                    <div
                                        css={s.dropdownItem}
                                        onClick={() => window.open("https://sodamap.co.kr:8448", '_blank')}
                                    >
                                        AI방역 플랫폼
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* <CommonButton
                            variant="navigation"
                            navigationType="address"
                            startIcon={<Map />}
                            onClick={() => window.open('https://sodamap.co.kr:8449/', '_blank')}
                        >
                            주소찾기
                        </CommonButton> */}
                    </div>
                </div>
                <GalleryLinkMenu />
            </div>
            <div css={s.mapLinkImgContainer}>
                <header>▼ Start the map</header>
                <div css={s.linkImgBox}>
                    <img
                        css={s.linkImg}
                        onClick={() => onClickMapLinkImg('202')}
                        src="images/start the map-1.png"
                        alt="경제구조"
                    />
                    <img
                        css={s.linkImg}
                        onClick={() => onClickMapLinkImg('404')}
                        src="images/start the map-2.png"
                        alt="사회/복지"
                    />
                    <img
                        css={s.linkImg}
                        onClick={() => onClickMapLinkImg('106')}
                        src="images/start the map-3.png"
                        alt="인구특성"
                    />
                    <img
                        css={s.linkImg}
                        onClick={() => onClickMapLinkImg('301')}
                        src="images/start the map-4.png"
                        alt="재정/세금"
                    />
                    {/* CommonButton 사용한 개선안은 나중에 사용할 수 있기 때문에 보관함
                    
                    <CommonButton
                        variant="image"
                        size="startTheMap"
                        layout="vertical"
                        onClick={() => onClickMapLinkImg('202')}
                        customStyles={s.economicStructureButton}
                    >
                        <div className="icon-area">
                            <TrendingUp />
                        </div>
                        <div className="text-area">경제구조</div>
                    </CommonButton>

                    <CommonButton
                        variant="image"
                        size="startTheMap"
                        layout="vertical"
                        onClick={() => onClickMapLinkImg('404')}
                        customStyles={s.socialWelfareButton}
                    >
                        <div className="icon-area">
                            <People />
                        </div>
                        <div className="text-area">사회/복지</div>
                    </CommonButton>

                    <CommonButton
                        variant="image"
                        size="startTheMap"
                        layout="vertical"
                        onClick={() => onClickMapLinkImg('106')}
                        customStyles={s.populationButton}
                    >
                        <div className="icon-area">
                            <Psychology />
                        </div>
                        <div className="text-area">인구특성</div>
                    </CommonButton>

                    <CommonButton
                        variant="image"
                        size="startTheMap"
                        layout="vertical"
                        onClick={() => onClickMapLinkImg('301')}
                        customStyles={s.financeTaxButton}
                    >
                        <div className="icon-area">
                            <AccountBalance />
                        </div>
                        <div className="text-area">재정/세금</div>
                    </CommonButton>

                    */}
                </div>
            </div>
        </div>
    );
};

export default Main;
